/**
 * @description 验证小数的最大长度
 * @param {string|number} value  验证数字
 * @param {number} len           小数的最大位数 默认为2
 * @returns {string}
 */
export const verifyDecimal = (value, len = 2) =>{
  let str = ''
  const arr = `${value}`.split('.')
  if (arr.length > 1 && arr[1].length > len) {
    str = `数字精确到小数点后${len}位`
  }
  return str
}

/**
 * @description 验证数字
 * @param {string|number} value  验证数字
 * @param {boolean} isInteger    是否验证整数
 * @param {string} empty         空值时的提示
 * @returns
 */
export const verifyNumber = (value, isInteger = false, empty) =>{
  let str = '', v = Number(value)
  if (value === '') {
    str = empty || '请输入'
  } else if (isNaN(v)) {
    str = '请输入数字'
  } else if (isInteger && !Number.isInteger(v)) {
    str = '请输入整数'
  }
  return str
}

export const verifyPositive = (value) => {
  let str = '', v = Number(value)
  if (value === '') {
    str = '请输入'
  } else if (isNaN(v)) {
    str = '请输入数字'
  } else if (v < 0) {
    str = '请输入正数'
  }
  return str
}

/**
 * @description 验证值是否为数字，且是否为符合小数位数
 * @param {string|number} value         验证数字
 * @param {object} custom               自定义参数
 * @param {string} custom.empty         空值时的提示
 * @param {boolean} custom.isInteger    是否验证整数
 * @param {number} custom.len           小数的最大位数 默认为2
 * @param {boolean} custom.positive     是否验证正数
 * @returns
 */
export const verifyIsNumber = (value, custom = {}) => {
  const { empty, isInteger = false, len = 2, positive = false } = custom
  let str = ''
  const v1 = verifyNumber(value, isInteger, empty)
  const v2 = verifyDecimal(value, len)
  const v3 = positive ? verifyPositive(value) : ''
  if (v1) {
    str = v1
  } else if (v3) {
    str = v3
  } else if (v2) {
    str = v2
  }
  return str
}

/**
 * @description 自定义验证数字的大小
 * @param {string|number|Array} value 验证数据
 * @param {Function} callback 返回函数
 * @param {object} custom 自定义参数
 * @param {boolean} custom.isInteger  验证整数
 * @param {boolean} custom.empty      空值时的提示
 * @param {number} custom.len         小数的位数
 * @param {number} custom.min         最小值
 * @param {number} custom.max         最大值
 * @param {boolean} custom.equalToMin 是否等于最小值
 * @param {boolean} custom.equalToMax 是否等于最大值
 */
export const validateNumber = (value, callback, custom = {}) => {
  const { isInteger = false, len, min, max, equalToMin = false, equalToMax = false, empty, type = 'text' } = custom
  const newType = ['symbol', 'text', 'math'].includes(type) ? type : 'text'
  if (empty && value === '') {
    callback(new Error(empty))
  }
  const v1 = verifyNumber(value, isInteger)
  v1 && callback(new Error(v1))
  const showText = isInteger ? '整数' : '数字'
  if (isNaN(Number(min)) && isNaN(Number(max))) {
    callback()
  } else if (isNaN(Number(max)) && !isNaN(Number(min)) && (equalToMin ? Number(value) < min : Number(value) <= min)){
    let symbolText = ''
    if (newType === 'text') {
      symbolText = equalToMin ? '大于等于' : '大于'
    } else {
      symbolText = equalToMin ? '≥' : '>'
    }
    callback(new Error(`请输入${symbolText}${min}的${showText}`))
  } else if (isNaN(Number(min)) && !isNaN(Number(max)) && (equalToMax ? Number(value) > max : Number(value) >= max)){
    let symbolText = ''
    if (newType === 'text') {
      symbolText = equalToMax ? '小于等于' : '小于'
    } else {
      symbolText = equalToMax ? '≤' : '<'
    }
    callback(new Error(`请输入${symbolText}${max}的${showText}`))
  } else if ((equalToMin ? Number(value) < min : Number(value) <= min) || (equalToMax ? Number(value) > max : Number(value) >= max)){
    (!equalToMin && !equalToMax) && callback(new Error(`请输入${min}-${max}以内的${showText}`))
    let symbolText = ''
    if (newType === 'text') {
      symbolText = `请输入${equalToMin ? '大于等于' : '大于'}${min}${equalToMin ? '且' : ''}${equalToMax ? '小于等于' : '小于'}${max}的${showText}`
    } else if (newType === 'symbol') {
      symbolText = `请输入${min}${equalToMin ? '≤' : '<'}x${equalToMax ? '≤' : '<'}${max}的${showText}`
    } else {
      symbolText = `请输入${equalToMin ? '[' : '('}${min},${max}${equalToMax ? ']' : ')'}以内的${showText}`
    }
    callback(new Error(symbolText))
  } else {
    const v2 = verifyDecimal(value, len)
    v2 && callback(new Error(v2))
    callback()
  }
}

/**
 * @description 自定义验证身份证号
 * @param {*} value
 * @param {*} callback
 */
export const validateIdCard = (value, callback) => {
  if (!value) return callback(new Error('请输入身份证号'))
  const reg1 = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/
  const reg2 = /.*x$/
  if (!reg1.test(value)) return callback(new Error('请输入正确的身份证号'))
  if (reg2.test(value)) return callback(new Error('最后一位x需要大写'))
  callback()
}

/**
 * @description 自定义验证身份证号
 * @param {*} value
 * @param {*} callback
 * @returns
 */
export const validatePassport = (value, callback) => {
  if (!value) return callback(new Error('请输入护照号'))
  const reg = /^[A-Za-z0-9]{9}$/
  if (!reg.test(value)) return callback(new Error('请输入正确的护照号'))
  callback()
}

/**
 * @description 自定义验证手机号
 * @param {*} value
 * @param {*} callback
 * @returns
 */
export const validatePhone = (value, callback) => {
  if (!value) return callback(new Error('请输入手机号'))
  if (!/^1\d{10}$/.test(value)) return callback(new Error('手机号格式错误'))
  callback()
}
